import {
  ContainerRelevantItemsGraphQL,
  ContainerRelevantItemsGraphQL_RelevantItemsNode_tagged as TaggedItem,
  ContainerRelevantItemsGraphQL_RelevantItemsNode_filesMentioning as FilesMentioningItem,
} from 'relevant-items/containers/__generated/ContainerRelevantItemsGraphQL';
import {ContainerRelevantItemsFilterOption} from 'relevant-items/containers/ContainerRelevantItemsFilteredTableState';
import {
  getAnyDescriptionMentionRow,
  getBiologicalEntityDescriptionMentionRow,
  getEntryMentionRow,
  getFieldReferenceTaggedRows,
  getSupportedRelevantItems,
  getTaskGroupsRows,
  getWorksheetStepMentionRow,
} from 'relevant-items/shared/RelevantItemsRowBuilderUtils';
import {RelevantRowData} from 'relevant-items/shared/RelevantItemsTableUtils';
import APIUtil from 'util/APIUtil';

const CONTAINER_LABEL = 'Container';

type TaggedStorableItem = ContainerRelevantItemsGraphQL['taggedStorables'][number];

function getTaggedRowsForFilter(
  supportedTagged: Array<TaggedItem>,
  itemsToShow: ContainerRelevantItemsFilterOption
): Array<RelevantRowData> {
  switch (itemsToShow) {
    case ContainerRelevantItemsFilterOption.ALL:
      return getFieldReferenceTaggedRows(supportedTagged, CONTAINER_LABEL);
    case ContainerRelevantItemsFilterOption.ENTITIES:
      return getFieldReferenceTaggedRows(
        supportedTagged.filter((item) => APIUtil.isBiologicalEntity(item.id)),
        CONTAINER_LABEL
      );
    case ContainerRelevantItemsFilterOption.ENTRIES:
      return getFieldReferenceTaggedRows(
        supportedTagged.filter((item) => APIUtil.isEntry(item.id)),
        CONTAINER_LABEL
      );
    default:
      return [];
  }
}

function getInventoryTaggedRowsForFilter(
  taggedStorables: Array<TaggedStorableItem>,
  itemsToShow: ContainerRelevantItemsFilterOption
): Array<RelevantRowData> {
  switch (itemsToShow) {
    case ContainerRelevantItemsFilterOption.ALL:
    case ContainerRelevantItemsFilterOption.INVENTORY:
      return getFieldReferenceTaggedRows(taggedStorables, CONTAINER_LABEL);
    default:
      return [];
  }
}

function getFilesMentioningRowForItem(
  item: FilesMentioningItem,
  itemsToShow: ContainerRelevantItemsFilterOption
): RelevantRowData | null {
  switch (itemsToShow) {
    case ContainerRelevantItemsFilterOption.ALL:
      return (
        getWorksheetStepMentionRow(item, CONTAINER_LABEL) ??
        getEntryMentionRow(item, CONTAINER_LABEL) ??
        getAnyDescriptionMentionRow(item, CONTAINER_LABEL)
      );
    case ContainerRelevantItemsFilterOption.WORKSHEET_STEPS:
      return getWorksheetStepMentionRow(item, CONTAINER_LABEL);
    case ContainerRelevantItemsFilterOption.ENTRIES:
      return getEntryMentionRow(item, CONTAINER_LABEL);
    case ContainerRelevantItemsFilterOption.ENTITIES:
      return getBiologicalEntityDescriptionMentionRow(item, CONTAINER_LABEL);
    case ContainerRelevantItemsFilterOption.PROCESSES:
    case ContainerRelevantItemsFilterOption.INVENTORY:
      return null;
    default:
      throw new Error('Invalid relevant items filter option');
  }
}

/**
 * Filters the files mentioning items to their respective types and returns an array of the data for the table.
 */
function getFilesMentioningRowsForFilter(
  supportedFilesMentioning: Array<FilesMentioningItem>,
  itemsToShow: ContainerRelevantItemsFilterOption
): Array<RelevantRowData> {
  return supportedFilesMentioning.flatMap((item) => {
    const row = getFilesMentioningRowForItem(item, itemsToShow);
    return row != null ? [row] : [];
  });
}

function getTaskGroupsRowsForFilter(
  processOutputs: ContainerRelevantItemsGraphQL['processOutputs'],
  processTasks: ContainerRelevantItemsGraphQL['processTasks'],
  itemsToShow: ContainerRelevantItemsFilterOption
): Array<RelevantRowData> {
  if (
    itemsToShow !== ContainerRelevantItemsFilterOption.ALL &&
    itemsToShow !== ContainerRelevantItemsFilterOption.PROCESSES
  ) {
    return [];
  }
  return getTaskGroupsRows(processOutputs, processTasks, CONTAINER_LABEL);
}

/**
 * Creates table rows for a container's relevant items.
 *
 * Takes in the active filter option and transforms the GraphQL relevant items data and returns an array of
 * the data for the tab. If ELN_ENABLE_DOCUMENT_NOTE_REFERENCES is false, then worksheet steps will not
 * be included in the table.
 */
export function getContainerRelevantItemRowData(
  itemsToShow: ContainerRelevantItemsFilterOption,
  relevantItemsGraphQL: ContainerRelevantItemsGraphQL
): Array<RelevantRowData> {
  const {filesMentioning, processOutputs, processTasks, tagged, taggedStorables} = relevantItemsGraphQL;
  const {supportedTagged, supportedFilesMentioning} = getSupportedRelevantItems(tagged, filesMentioning);

  return [
    ...getTaggedRowsForFilter(supportedTagged, itemsToShow),
    ...getInventoryTaggedRowsForFilter(taggedStorables, itemsToShow),
    ...getFilesMentioningRowsForFilter(supportedFilesMentioning, itemsToShow),
    ...getTaskGroupsRowsForFilter(processOutputs, processTasks, itemsToShow),
  ];
}
